import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Loader2, Columns3, FileText, Trash2, ArrowRight, UploadCloud } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const statusPill = {
  draft: "bg-secondary text-muted-foreground",
  sent: "bg-brand-soft text-brand",
  awarded: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200",
  closed: "bg-secondary text-muted-foreground",
};

export default function Qompare() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", project: "", currency: "AED" });
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dropping, setDropping] = useState(false);
  const dropRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get("/buyer/rfqs", { params: { kind: "qompare" } });
      setRows(data.rfqs);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not load your comparisons");
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => /\.(pdf|xlsx|xls|csv)$/i.test(f.name));
    if (!files.length) { toast.error("Drop PDF or Excel quotation files"); return; }
    setDropping(true);
    try {
      const title = `Quote comparison — ${new Date().toLocaleDateString()}`;
      const { data } = await api.post("/buyer/rfqs", { title, items: [], kind: "qompare", currency: "AED" });
      let ok = 0; const failed = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const vendor = f.name.replace(/\.[^.]+$/, "").slice(0, 60);
        try {
          await api.post(`/buyer/rfqs/${data.id}/quotes/upload?vendor_name=${encodeURIComponent(vendor)}`, fd);
          ok += 1;
        } catch (err) {
          failed.push(f.name);
        }
      }
      if (ok) toast.success(`${ok} quote${ok === 1 ? "" : "s"} added — now add your BOQ reference, vendor & project details`);
      if (failed.length) toast.warning(`${failed.length} file(s) couldn't be read automatically — add those totals manually inside`);
      navigate(`/rfqs/${data.id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not start the comparison");
    } finally {
      setDropping(false);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/buyer/rfqs", { ...form, items: [], kind: "qompare" });
      toast.success("Comparison created — add your BOQ, then drop in each vendor's quote");
      navigate(`/rfqs/${data.id}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not create the comparison");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete comparison "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/buyer/rfqs/${id}`);
      toast.success("Comparison deleted");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (!rows) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div data-testid="qompare-page" className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Matiqs Qompare</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-2">Compare quotes</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Already got quotes by email? Drop them in here and see every price side by side — no RFQ needed, free.
          </p>
        </div>
        <Button variant="outline" data-testid="new-comparison-button" onClick={() => setCreating(!creating)} className="gap-2 font-semibold h-11">
          <Plus className="w-4 h-4" /> Blank comparison
        </Button>
      </div>

      <div>
        <div
          data-testid="qompare-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); if (!dropping) handleFiles(e.dataTransfer.files); }}
          onClick={() => !dropping && dropRef.current?.click()}
          className={`rounded-3xl border-2 border-dashed p-10 sm:p-14 text-center cursor-pointer transition-colors ${dragging ? "border-primary bg-brand-soft/50" : "border-border hover:border-primary/50 bg-card"}`}
        >
          <input ref={dropRef} type="file" hidden multiple accept=".pdf,.xlsx,.xls,.csv"
                 data-testid="qompare-file-input" onChange={(e) => handleFiles(e.target.files)} />
          {dropping ? (
            <div className="flex flex-col items-center gap-3 text-primary">
              <Loader2 className="w-9 h-9 animate-spin" />
              <p className="font-semibold">Reading your quotes and building the comparison…</p>
            </div>
          ) : (
            <>
              <div className="w-14 h-14 rounded-2xl bg-brand-soft text-brand flex items-center justify-center mx-auto">
                <UploadCloud className="w-7 h-7" />
              </div>
              <h2 className="font-display text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white mt-5">
                Drop your quotation files here
              </h2>
              <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                Straight from your email inbox — PDF or Excel, one or many. We read the prices and line them up for you.
              </p>
              <Button data-testid="qompare-browse-button" type="button" className="mt-6 h-11 px-6 font-semibold gap-2"
                      onClick={(e) => { e.stopPropagation(); dropRef.current?.click(); }}>
                <UploadCloud className="w-4 h-4" /> Choose quote files
              </Button>
            </>
          )}
        </div>
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-5 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><span className="w-5 h-5 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center">1</span> Drop the quotes you got by email</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-5 h-5 rounded-full bg-secondary text-foreground text-[10px] font-bold flex items-center justify-center">2</span> Add your BOQ reference</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-5 h-5 rounded-full bg-secondary text-foreground text-[10px] font-bold flex items-center justify-center">3</span> Add vendor & project details</span>
        </div>
      </div>

      {creating && (
        <motion.form
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={create}
          data-testid="new-comparison-form"
          className="bg-card border border-border rounded-2xl p-6 grid sm:grid-cols-2 gap-4"
        >
          <div className="sm:col-span-2">
            <Label htmlFor="cmp-title">Comparison title</Label>
            <Input id="cmp-title" data-testid="comparison-title-input" required value={form.title}
                   onChange={(e) => setForm({ ...form, title: e.target.value })}
                   placeholder="e.g. Tower B — Aluminium & Glazing" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="cmp-project">Project (optional)</Label>
            <Input id="cmp-project" data-testid="comparison-project-input" value={form.project}
                   onChange={(e) => setForm({ ...form, project: e.target.value })}
                   placeholder="e.g. Marina Heights" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="cmp-cur">Currency</Label>
            <Input id="cmp-cur" data-testid="comparison-currency-input" value={form.currency}
                   onChange={(e) => setForm({ ...form, currency: e.target.value })}
                   placeholder="AED / $ / ₹" className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <p className="text-[11px] text-muted-foreground mb-3">
              Next: add your BOQ line items (or upload the Excel), then upload each vendor's PDF/Excel quote or type the total in — Matiqs lines them up for you.
            </p>
            <Button type="submit" data-testid="create-comparison-button" disabled={busy} className="h-11 w-full sm:w-auto sm:px-8 font-semibold gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create Comparison
            </Button>
          </div>
        </motion.form>
      )}

      {rows.length === 0 ? (
        <div data-testid="no-comparisons" className="bg-card border border-border rounded-2xl p-12 text-center">
          <Columns3 className="w-9 h-9 text-muted-foreground mx-auto" />
          <p className="font-semibold text-slate-900 dark:text-slate-100 mt-4">No comparisons yet</p>
          <p className="text-sm text-muted-foreground mt-1">Create one, add your BOQ, then drop in the quotes you already received.</p>
        </div>
      ) : (
        <div data-testid="comparison-list" className="space-y-3">
          {rows.map((r, i) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.04, 0.3) }}
              data-testid={`comparison-row-${i}`}
              className="bg-card border border-border rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:border-primary/40 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    data-testid={`comparison-title-${i}`}
                    onClick={() => navigate(`/rfqs/${r.id}`)}
                    className="font-display text-lg font-bold text-slate-900 dark:text-slate-100 hover:text-primary transition-colors text-left"
                  >
                    {r.title}
                  </button>
                  <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wide ${statusPill[r.status] || statusPill.draft}`}>
                    {r.status}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1.5 flex items-center gap-4 flex-wrap">
                  {r.project && <span>{r.project}</span>}
                  <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" />{r.item_count} items</span>
                  <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                    <Columns3 className="w-3 h-3" />{r.quote_count} quote{r.quote_count === 1 ? "" : "s"}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.quote_count > 0 && (
                  <Button variant="outline" data-testid={`open-compare-${i}`} onClick={() => navigate(`/compare/${r.id}`)} className="h-10 gap-2">
                    <Columns3 className="w-4 h-4" /> Compare
                  </Button>
                )}
                <Button data-testid={`open-comparison-${i}`} onClick={() => navigate(`/rfqs/${r.id}`)} className="h-10 gap-2 font-semibold">
                  Open <ArrowRight className="w-4 h-4" />
                </Button>
                <button
                  data-testid={`delete-comparison-${i}`}
                  onClick={() => remove(r.id, r.title)}
                  className="w-10 h-10 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors"
                  title="Delete comparison"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
